import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  const fetchCurrentUser = useCallback(async () => {
    if (!localStorage.getItem('access_token')) {
      setLoading(false);
      return;
    }

    try {
      const res = await api.get('/auth/me/');
      setUser(res.data);
      if (res.data.employee) {
        setEmployee(res.data.employee);
      } else {
        setEmployee(null);
      }

      // Fetch onboarding status
      try {
        const statusRes = await api.get('/onboarding/status/');
        setOnboardingStatus(statusRes.data);
      } catch (err) {
        console.error('Failed to fetch onboarding status:', err);
      }

    } catch (err) {
      console.error('Failed to fetch user:', err);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (username, password) => {
    const res = await api.post('/auth/login/', { username, password });
    const { access, refresh, role } = res.data;

    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    setToken(access);

    await fetchCurrentUser();
    return { role };
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setEmployee(null);
    setToken(null);
    setOnboardingStatus(null);
  };

  const refreshOnboardingStatus = async () => {
    try {
      const res = await api.get('/onboarding/status/');
      setOnboardingStatus(res.data);
    } catch (err) {
      console.error('Error refreshing onboarding status:', err);
    }
  };

  const isSuperAdmin = user?.role === 'SUPER_ADMIN' || user?.is_superuser;
  const isEmployee = user?.role === 'EMPLOYEE';

  return (
    <AuthContext.Provider value={{
      user,
      employee,
      token,
      loading,
      onboardingStatus,
      isSuperAdmin,
      isEmployee,
      login,
      logout,
      refreshCurrentUser: fetchCurrentUser,
      refreshOnboardingStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
